import { useQuery } from '@tanstack/react-query';

import {
	resyncPassengerBookings,
	useBusinessLayer,
} from '@mopla/business-logic';
import {
	extractData,
	getDriverLocationApi,
	GetDriverLocationResponses,
	throwOnStatusCodes,
} from '@mopla/data-models';
import { useLogger } from '@mopla/logger';

import { bookingMapDataKeys } from './queryKeys';
import { TBookingMapData } from './useBookingMapApi$';

const SLOW_POLL_INTERVAL_MS = 10000;
const FAST_POLL_INTERVAL_MS = 3000;
const MAX_RETRIES = 3;

export const getBookingMapData = async (
	legId: string
): Promise<TBookingMapData> => {
	try {
		const response = await getDriverLocationApi({
			...throwOnStatusCodes([204, 422]),
			path: { uuid: legId },
		});

		return extractData<GetDriverLocationResponses, 200>(200)(response);
	} catch (e) {
		const statusCode = (e as { response?: { status?: number } })?.response
			?.status;

		/**
		 * The BE restricts access to the driver's geo via 204/422 — available
		 * from ~20 minutes before the ride until pickup/check-in.
		 */
		if (statusCode === 204 || statusCode === 422) {
			return null;
		}

		throw e;
	}
};

/**
 * TanStack Query variant of useBookingMapApiPolling$, with a working
 * `enabled` toggle (the RxJS version's shared scan/shareReplay pipeline
 * could get permanently stuck once disabled and never resume on re-enable).
 *
 * Polls slowly (10s) until the api first returns data, then switches to
 * fast polling (3s). A gap in the geo drops it back to slow polling; `enabled`
 * is what stops it, so the caller owns the tracking window.
 */
export const useBookingMapApiPollingV2 = (
	legId: string,
	enabled = true
): TBookingMapData => {
	const bl = useBusinessLayer();
	const logger = useLogger('useBookingMapApiPollingV2');

	const { data } = useQuery({
		queryKey: bookingMapDataKeys.leg(legId),
		queryFn: async () => {
			const result = await getBookingMapData(legId);

			/** Resync bookings data on tick in case of operator updates */
			bl.dispatch(resyncPassengerBookings());

			return result;
		},
		enabled: enabled && Boolean(legId),
		retry: MAX_RETRIES,
		retryDelay: (_failureCount, error) => {
			logger.error('Failed to poll booking map data', error);
			return 500;
		},
		refetchInterval: (query) => {
			if (query.state.status === 'error') {
				return false;
			}

			return query.state.data ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
		},
	});

	return data ?? null;
};
